export const vatCountriesEU = {
  AT: {
    name: "USt",
    examples: ["U12345678"],
    regex: /^U\d{8}$/,
  },
  BE: {
    name: "BTW, TVA, NWSt",
    examples: ["1234567890"],
    regex: /^\d{10}$/,
  },
  BG: {
    name: "ДДС",
    examples: ["123456789", "1234567890"],
    regex: /^\d{9,10}$/,
  },
  HR: {
    name: "PDV",
    examples: ["12345678901"],
    regex: /\d{11}/,
  },
  CY: {
    name: "ΦΠΑ",
    examples: ["12345678X"],
    regex: /^\d{8}\p{Alpha}$/u,
  },
  CZ: {
    name: "DPH",
    examples: ["12345678", "123456789", "1234567890"],
    regex: /(?:\d{1,3})?\d{8,10}$/, //8, 9 or 10 characters. If more than 10 characters are provided delete the first 3 as these are a tax code
  },
  DK: {
    name: "moms",
    examples: ["12345678"],
    regex: /\d{8}/, // 8 characters
  },
  EE: {
    name: "km",
    examples: ["123456789"],
    regex: /\d{9}/, // 9 characters
  },
  FI: {
    name: "ALV, Moms",
    examples: ["12345678"],
    regex: /\d{8}/, // 8 characters
  },
  FR: {
    name: "TVA",
    regex:
      /(\d{11}|[ABCDEFGHJKLMNPQRSTUVYYZ]\d{10}|\d[ABCDEFGHJKLMNPQRSTUVYYZ]\d{9}|[ABCDEFGHJKLMNPQRSTUVYYZ]{2}\d{9})$/,
    // 11 characters.
    // May include alphabetical characters (any except O or I)
    // as first or second
    // or first and second characters
    examples: ["12345678901", "X1234567890", "1X123456789, XX123456789"],
  },
  DE: {
    name: "MwSt., Ust.",
    examples: ["123456789"],
    regex: /\d{9}/, // 9 characters
  },
  GR: {
    name: "ΦΠΑ",
    regex: /\d{9}/, // 9 characters
    examples: ["123456789"],
  },
  HU: {
    name: "ÁFA",
    regex: /\d{8}/, // 8 characters
    examples: ["12345678"],
  },
  IE: {
    name: "CBL",
    examples: ["1234567X", "1X23456X", "1234567XX"],
    regex: /(\d{7}[A-Z]|\d[A-Z]\d{5}[A-Z]|\d{7}[A-Z]{2})$/, // 8 or 9 characters. Includes one or two alphabetical characters (last, or second and last, or last 2)
  },
  IT: {
    name: "IVA",
    regex: /(\d{11})/, // 11 characters
    examples: ["12345678901"],
  },
  LV: {
    name: "PVN",
    examples: ["12345678901"],
    regex: /\d{11}/, // 11 characters
  },
  LT: {
    name: "PVM",
    examples: ["123456789", "123456789012"],
    regex: /(\d{9}|\d{12})/, // 9 or 12 characters
  },
  LU: {
    name: "TVA",
    examples: ["12345678"],
    regex: /\d{8}/, // 8 characters
  },
  MT: {
    name: "VAT",
    examples: ["12345678"],
    regex: /\d{8}/, // 8 characters
  },
  NL: {
    name: "BTW",
    examples: ["123456789B01"],
    regex: /\d{9}B\d{2}/, // 12 characters. The tenth character is always B
  },
  PL: {
    name: "PTU",
    examples: ["1234567890"],
    regex: /\d{10}/, // 10 characters
  },
  PT: {
    name: "IVA",
    examples: ["123456789"],
    regex: /\d{9}/, // 9 characters
  },
  RO: {
    name: "TVA",
    examples: [
      "12",
      "123",
      "1234",
      "12345",
      "123456",
      "1234567",
      "12345678",
      "123456789",
      "1234567890",
    ],
    regex: /\d{2,10}/, // From 2 to 10 characters
  },
  SK: {
    name: "DPH",
    examples: ["1234567890"],
    regex: /\d{10}/, // 10 characters
  },
  SI: {
    name: "DDV",
    examples: ["12345678"],
    regex: /\d{8}/, // 8 characters
  },
  ES: {
    name: "IVA",
    examples: ["X12345678", "12345678X", "X1234567X"],
    // Includes one or two alphabetical characters (first or last or first and last)
    regex: /([A-Z]\d{8}|\d{8}[A-Z]|[A-Z]\d{7}[A-Z])/, //  9 characters.
  },
  SE: {
    name: "Mom s",
    examples: ["123456789012"],
    regex: /\d{12}/, // 12 character
  },
} as const;

export type VATCountryEU = keyof typeof vatCountriesEU;

export const isVATCountryEU = (country: unknown): country is VATCountryEU =>
  typeof country === "string" && Object.keys(vatCountriesEU).includes(country);
export const checkCountryVAT = (country: VATCountryEU) =>
  country === "GR"
    ? checkGreekVATNumber
    : isVATCountryEU(country)
    ? vatCountriesEU[country].regex.test
    : fail(new Error(`invalid VAT country ${country}`));

const fail = (err: Error) => {
  throw err;
};

const charCodeZero = "0".charCodeAt(0);
export const checkGreekVATNumber = (input: string): boolean => {
  if (input.length === 9 && input != "000000000") {
    let sum = 0;
    for (let i = 0; i < 8; i++) {
      const c = input.charCodeAt(i) - charCodeZero;
      sum += 0 <= c && c <= 9 ? c << (8 - i) : NaN;
    }
    const checksum = input.charCodeAt(8) - charCodeZero;
    return (sum % 11) % 10 === checksum;
  }
  return false;
};

export const vatCountryCodesEU = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LT",
  "LU",
  "MT",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
] as const;

function expectAssignable<T>(_: T): void {}

function expectExtends<T, U extends T>(): void {}

expectAssignable<readonly VATCountryEU[]>(vatCountryCodesEU);

type codes = typeof vatCountryCodesEU[number];
expectExtends<VATCountryEU, codes>();
expectExtends<codes, VATCountryEU>();
